/**
 * Auth Store
 * 
 * Zustand store for authentication state management.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { authApi, impersonationApi } from '@/lib/api';
import { useLanguageStore } from '@/stores/languageStore';
import { useGeneralInfoStore } from '@/stores/generalInfoStore';

// User type
export interface User {
  id: number;
  firstName: string;
  lastName: string;
  userName: string;
  email: string;
  photo?: string | null;
  photoUrl?: string;
  position?: string;
  orgUnit?: string;
  /** 'attendance_only' means user can only access the attendance module */
  type?: string;
  /** Control center (/system) – master users.IsPlatformAdmin of the current identity */
  isPlatformAdmin?: boolean;
  /** Impersonation – IsPlatformAdmin of the real admin (also while impersonating) */
  canImpersonate?: boolean;
}

// Tenant type
export interface Tenant {
  id: number;
  code: string;
  database?: string;
  role?: string;
  is_primary?: boolean;
  /** Display name from tenant settings (Title or CompanyName) */
  name?: string;
}

// Permission type
export interface Permission {
  id: number;
  app: string;
  level: number;
  fromGroup: number;
}

// Auth state type
interface AuthState {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  require2FA: boolean;
  require2FASetup: boolean;
  qrCodeUrl: string | null;
  secret: string | null;
  pendingUserId: number | null;
  
  // Tenant state
  currentTenant: Tenant | null;
  availableTenants: Tenant[];
  needsTenantCreation: boolean;
  settings: Record<string, string>;
  permissions: Permission[];
  
  // Actions
  login: (userName: string, password: string) => Promise<boolean>;
  verify2FA: (token: string) => Promise<boolean>;
  logout: () => Promise<void>;
  clearAuthState: () => void; // Clear local state without API call
  checkAuth: () => Promise<boolean>;
  refreshSession: () => Promise<void>;
  clearError: () => void;
  setUser: (user: User | null) => void;
  /** Hydrate from general-info response (user, tenant, tenants, settings, permissions) */
  setFromGeneralInfo: (payload: { user: User | null; tenant?: Tenant | null; tenants?: Tenant[]; needsTenantCreation?: boolean; settings?: Record<string, string>; permissions?: Permission[] }) => void;
  /** Hydrate from GET /api/auth/user (when no tenant / general-info not available) */
  setFromUserResponse: (payload: { user: User; tenant?: { id?: number; code?: string; database?: string } | null; tenants?: Tenant[]; needsTenantCreation?: boolean; settings?: Record<string, string>; permissions?: Permission[] }) => void;
  /** Set current tenant after creating a new one (e.g. from create-tenant page) so dashboard shows it without refetch */
  setCurrentTenantFromCreation: (tenant: { id: number; code: string; database?: string; name?: string }) => void;
  switchTenant: (tenantCode: string) => Promise<boolean>;
  /** Refetch user/tenant from GET /api/auth/user and update store (e.g. after end impersonation) */
  refreshUserAndTenant: () => Promise<void>;
  
  // Impersonation (platform admins only)
  isImpersonating: boolean;
  setFromImpersonationStatus: (payload: { active: boolean }) => void;
  startImpersonation: (tenantId: number, userId: number) => Promise<boolean>;
  endImpersonation: () => Promise<void>;
  checkImpersonationStatus: () => Promise<void>;
  
  // OAuth actions
  loginWithGoogle: () => Promise<void>;
  loginWithMicrosoft: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      require2FA: false,
      require2FASetup: false,
      qrCodeUrl: null,
      secret: null,
      pendingUserId: null,
      
      // Tenant state
      currentTenant: null,
      availableTenants: [],
      needsTenantCreation: false,
      settings: {},
      permissions: [],
      isImpersonating: false,
      
      // Login action
      login: async (userName: string, password: string) => {
        set({ isLoading: true, error: null });
        
        try {
          const response = await authApi.login(userName, password);
          const data = response.data;
          
          if (!data.success) {
            set({ 
              isLoading: false, 
              error: data.error || 'Login failed' 
            });
            return false;
          }
          
          // Check if tenant selection is required
          if (data.requireTenantSelection) {
            set({
              isLoading: false,
              availableTenants: data.tenants || [],
            });
            // Return false to indicate tenant selection is needed
            return false;
          }
          
          // Check if 2FA is required
          if (data.require2FA) {
            set({
              isLoading: false,
              require2FA: true,
              pendingUserId: data.userId,
            });
            return false;
          }
          
          // Check if 2FA setup is required
          if (data.require2FASetup) {
            set({
              isLoading: false,
              require2FASetup: true,
              qrCodeUrl: data.qrCodeUrl,
              secret: data.secret,
              pendingUserId: data.userId,
            });
            return false;
          }
          
          // Login successful - extract user and tenant info
          const userData = data.user as Record<string, unknown> | undefined;
          const user: User = {
            id: data.user?.id || data.userId || 0,
            firstName: data.user?.firstName || data.user?.FirstName || data.firstName || '',
            lastName: data.user?.lastName || data.user?.LastName || data.lastName || '',
            userName: data.user?.userName || data.user?.UserName || data.userName || userName,
            email: data.user?.email || data.user?.eMail || data.email || '',
            photo: (userData?.photo || userData?.Photo || null) as string | null,
            photoUrl: (userData?.photoUrl || userData?.photo || userData?.Photo || '') as string,
          };
          
          const tenant: Tenant | null = data.tenant ? {
            id: data.tenant.id,
            code: data.tenant.code,
          } : null;
          
          set({
            isLoading: false,
            isAuthenticated: true,
            user,
            currentTenant: tenant,
            settings: data.settings || {},
            require2FA: false,
            require2FASetup: false,
          });
          
          return true;
          
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.response?.data?.error || 'Login failed',
          });
          return false;
        }
      },
      
      // Verify 2FA action
      verify2FA: async (token: string) => {
        const { pendingUserId } = get();
        
        if (!pendingUserId) {
          set({ error: 'No pending user ID' });
          return false;
        }
        
        set({ isLoading: true, error: null });
        
        try {
          const response = await authApi.verify2FA(pendingUserId, token);
          const data = response.data;
          
          if (!data.success) {
            set({
              isLoading: false,
              error: data.error || 'Invalid 2FA code',
            });
            return false;
          }
          
          // 2FA verified successfully
          set({
            isLoading: false,
            isAuthenticated: true,
            user: {
              id: data.userId || 0,
              firstName: data.firstName || '',
              lastName: data.lastName || '',
              userName: data.userName || '',
              email: data.email || '',
            },
            require2FA: false,
            require2FASetup: false,
            qrCodeUrl: null,
            secret: null,
            pendingUserId: null,
          });
          
          return true;
          
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.response?.data?.error || 'Invalid 2FA code',
          });
          return false;
        }
      },
      
      // Logout action
      logout: async () => {
        set({ isLoading: true });
        
        try {
          await authApi.logout();
        } catch (error) {
          // Ignore logout errors
        }
        
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          require2FA: false,
          require2FASetup: false,
          qrCodeUrl: null,
          secret: null,
          pendingUserId: null,
      currentTenant: null,
      availableTenants: [],
      needsTenantCreation: false,
      settings: {},
      permissions: [],
    });
  },
      
      // Clear auth state without API call (for session expired scenarios)
      clearAuthState: () => {
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          require2FA: false,
          require2FASetup: false,
          qrCodeUrl: null,
          secret: null,
          pendingUserId: null,
      currentTenant: null,
      availableTenants: [],
      needsTenantCreation: false,
      settings: {},
      permissions: [],
    });
  },
      
      // Check authentication status (only auth/check; user etc. come from general-info in layout)
      checkAuth: async () => {
        set({ isLoading: true });
        try {
          const response = await authApi.check();
          const data = response.data;
          if (data.authenticated) {
            set({ isLoading: false, isAuthenticated: true });
            return true;
          }
          set({ isLoading: false, isAuthenticated: false, user: null });
          return false;
        } catch {
          set({ isLoading: false, isAuthenticated: false, user: null });
          return false;
        }
      },
      
      // Refresh session
      refreshSession: async () => {
        try {
          await authApi.refresh();
        } catch (error) {
          // Session refresh failed, logout
          get().logout();
        }
      },
      
      // Clear error
      clearError: () => {
        set({ error: null });
      },
      
      // Set user
      setUser: (user: User | null) => {
        set({ user, isAuthenticated: !!user });
      },

      setFromGeneralInfo: (payload) => {
        const t = payload.tenant as { id?: number; code?: string; database?: string } | null | undefined;
        const tenant: Tenant | null = t
          ? { id: t.id ?? 0, code: t.code ?? '', database: t.database }
          : null;
        set({
          user: payload.user,
          isAuthenticated: !!payload.user,
          currentTenant: tenant,
          availableTenants: payload.tenants ?? [],
          needsTenantCreation: payload.needsTenantCreation ?? (payload.tenants ?? []).length === 0,
          settings: payload.settings ?? {},
          permissions: payload.permissions ?? [],
        });
      },

      setFromUserResponse: (payload) => {
        const user: User = {
          id: payload.user.id,
          firstName: payload.user.firstName ?? '',
          lastName: payload.user.lastName ?? '',
          userName: payload.user.userName ?? '',
          email: payload.user.email ?? '',
          photoUrl: payload.user.photoUrl,
          position: payload.user.position,
          orgUnit: payload.user.orgUnit,
          isPlatformAdmin: payload.user.isPlatformAdmin,
          canImpersonate: payload.user.canImpersonate,
        };
        const tenantPayload = payload.tenant as { id?: number; code?: string; database?: string } | null | undefined;
        const currentTenant: Tenant | null = tenantPayload?.code
          ? { id: tenantPayload.id ?? 0, code: tenantPayload.code, database: tenantPayload.database }
          : null;
        set({
          user,
          isAuthenticated: true,
          currentTenant,
          availableTenants: payload.tenants ?? [],
          needsTenantCreation: payload.needsTenantCreation ?? (payload.tenants?.length === 0),
          settings: payload.settings ?? {},
          permissions: payload.permissions ?? [],
        });
      },

      setCurrentTenantFromCreation: (tenant) => {
        const t: Tenant = {
          id: tenant.id,
          code: tenant.code,
          database: tenant.database,
          name: tenant.name,
        };
        const { availableTenants } = get();
        const exists = availableTenants.some((a) => a.code === t.code);
        set({
          currentTenant: t,
          availableTenants: exists ? availableTenants : [t, ...availableTenants],
          needsTenantCreation: false,
        });
      },

      // Switch tenant
      switchTenant: async (tenantCode: string) => {
        const { user } = get();
        
        if (!user) {
          set({ error: 'Not authenticated' });
          return false;
        }
        
        set({ isLoading: true, error: null });
        
        try {
          const response = await authApi.switchTenant(tenantCode, user.id);
          const data = response.data;
          
          if (!data.success) {
            set({
              isLoading: false,
              error: data.error || 'Failed to switch tenant',
            });
            return false;
          }
          
          // Update state with new tenant info
          const switchUserData = data.user as Record<string, unknown> | undefined;
          const newUser: User = {
            id: data.user?.id || user.id,
            firstName: data.user?.firstName || user.firstName,
            lastName: data.user?.lastName || user.lastName,
            userName: data.user?.userName || user.userName,
            email: data.user?.email || user.email,
            photo: (switchUserData?.photo || user.photo || null) as string | null,
            photoUrl: (switchUserData?.photoUrl || switchUserData?.photo || user.photoUrl || '') as string,
          };
          
          const tenant: Tenant | null = data.tenant ? {
            id: data.tenant.id,
            code: data.tenant.code,
          } : null;
          
          if (!tenant) {
            set({
              isLoading: false,
              error: 'No tenant data returned',
            });
            return false;
          }
          
          set({
            isLoading: false,
            user: newUser,
            currentTenant: tenant,
            settings: data.settings || {},
          });
          return true;
          
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.response?.data?.error || 'Failed to switch tenant',
          });
          return false;
        }
      },
      
      refreshUserAndTenant: async () => {
        try {
          const res = await authApi.getUser();
          const data = res.data as { success?: boolean; data?: Record<string, unknown>; tenant?: unknown; tenants?: unknown[]; needsTenantCreation?: boolean; settings?: Record<string, string>; permissions?: unknown[] };
          if (!data?.success || !data?.data) return;
          const u = data.data as { id: number; firstName?: string; lastName?: string; userName?: string; email?: string; photoUrl?: string; position?: string; orgUnit?: string; language?: string; isPlatformAdmin?: boolean; canImpersonate?: boolean };
          get().setFromUserResponse({
            user: {
              id: u.id,
              firstName: u.firstName ?? '',
              lastName: u.lastName ?? '',
              userName: u.userName ?? '',
              email: u.email ?? '',
              photoUrl: u.photoUrl,
              position: u.position,
              orgUnit: u.orgUnit,
              isPlatformAdmin: u.isPlatformAdmin,
              canImpersonate: u.canImpersonate,
            },
            tenant: data.tenant as { id?: number; code?: string; database?: string } | null | undefined,
            tenants: (data.tenants as Tenant[]) ?? [],
            needsTenantCreation: data.needsTenantCreation ?? (Array.isArray(data.tenants) && data.tenants.length === 0),
            settings: data.settings ?? {},
            permissions: (data.permissions as Permission[]) ?? [],
          });
          if (u.language && ['sk', 'cs', 'en'].includes(u.language)) {
            useLanguageStore.getState().setLanguageFromBackend(u.language as 'sk' | 'cs' | 'en');
          }
        } catch {
          // ignore
        }
      },
      
      setFromImpersonationStatus: (payload) => {
        set({ isImpersonating: payload.active });
      },
      
      startImpersonation: async (tenantId: number, userId: number) => {
        try {
          const res = await impersonationApi.start(tenantId, userId);
          const data = res.data as { success?: boolean; user?: Record<string, unknown>; tenant?: Record<string, unknown> };
          if (!data?.success || !data.user || !data.tenant) {
            return false;
          }
          const u = data.user as { id: number; userName?: string; firstName?: string; lastName?: string; email?: string };
          const t = data.tenant as { id: number; code: string; database?: string };
          const tenant = { id: t.id, code: t.code, database: t.database };
          set({
            isImpersonating: true,
            user: {
              id: u.id,
              firstName: (u.firstName as string) ?? '',
              lastName: (u.lastName as string) ?? '',
              userName: (u.userName as string) ?? '',
              email: (u.email as string) ?? '',
            },
            currentTenant: tenant,
            needsTenantCreation: false,
            availableTenants: [tenant],
          });
          await get().checkAuth();
          // Load general-info (company settings, sidebar, etc.) for impersonated tenant immediately
          try {
            const g = useGeneralInfoStore.getState();
            g.clear();
            await g.fetchGeneralInfo();
          } catch {
            // ignore
          }
          return true;
        } catch {
          return false;
        }
      },
      
      endImpersonation: async () => {
        try {
          await impersonationApi.end();
          set({ isImpersonating: false });
          await get().checkAuth();
          await get().refreshUserAndTenant();
        } catch {
          set({ isImpersonating: false });
        }
      },
      
      checkImpersonationStatus: async () => {
        try {
          const res = await impersonationApi.status();
          const data = res.data as { success?: boolean; active?: boolean };
          if (data?.success && data.active) {
            set({ isImpersonating: true });
            await get().checkAuth();
          } else {
            set({ isImpersonating: false });
          }
        } catch {
          set({ isImpersonating: false });
        }
      },
      
      // OAuth: Login with Google
      loginWithGoogle: async () => {
        try {
          const response = await authApi.googleRedirect();
          if (response.data.success && response.data.redirectUrl) {
            window.location.href = response.data.redirectUrl;
          }
        } catch (error: any) {
          set({ error: error.response?.data?.error || 'Google login failed' });
        }
      },
      
      // OAuth: Login with Microsoft
      loginWithMicrosoft: async () => {
        try {
          const response = await authApi.microsoftRedirect();
          if (response.data.success && response.data.redirectUrl) {
            window.location.href = response.data.redirectUrl;
          }
        } catch (error: any) {
          set({ error: error.response?.data?.error || 'Microsoft login failed' });
        }
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => sessionStorage),
        partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        currentTenant: state.currentTenant,
        availableTenants: state.availableTenants,
        needsTenantCreation: state.needsTenantCreation,
        settings: state.settings,
        permissions: state.permissions,
      }),
    }
  )
);
